import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { BILLING_QUERY_KEY, getBilling, type BillingView } from '../api/endpoints/planPayment';
import { paidUntilLine } from '../lib/planPayment';
import { planLabel } from '../lib/plan';
import { trialStatusLine } from '../lib/trial';
import { formatWholeDollars } from './PlanPicker';

/**
 * Membresía's payment line, under the plan: paid until when, a reported
 * payment waiting on Dale, or what is due with "Pagar ahora" into /pago.
 * Says nothing for Independientes or a server without the checkout.
 */
export function MembershipPayment() {
  const billing = useQuery({ queryKey: BILLING_QUERY_KEY, queryFn: getBilling, retry: false });
  const view = billing.data;
  if (!view?.quote) return null;
  const line = paymentLine(view);
  return (
    <div className="membership-payment">
      <div>
        <p className="status" data-tone={line.tone}>
          {line.status}
        </p>
        {line.detail ? <p className="membership-payment-detail">{line.detail}</p> : null}
      </div>
      {line.pay ? (
        <Link to="/pago" className="btn small">
          Pagar ahora
        </Link>
      ) : null}
    </div>
  );
}

type PaymentLine = { tone: 'good' | 'hold' | 'due' | 'off'; status: string; detail?: string; pay: boolean };

function paymentLine(view: BillingView): PaymentLine {
  if (view.latest?.status === 'reportado') {
    return { tone: 'hold', status: 'Pago reportado', detail: 'Lo confirmamos en unas horas.', pay: false };
  }
  if (view.coverage.status === 'al_dia' && view.coverage.lastCoveredMonth) {
    return { tone: 'good', status: paidUntilLine(view.coverage.lastCoveredMonth), pay: false };
  }
  // Nothing is due during the free trial: it is not "pendiente", and paying stays in /pago.
  const trial = trialStatusLine(view.trialEndsAt);
  if (trial) return { tone: 'off', status: trial, pay: false };
  const quote = view.quote!;
  const detail =
    quote.billingCycle === 'annual'
      ? `${formatWholeDollars(quote.amountCents)} por el año de ${planLabel(quote.plan)}`
      : `${formatWholeDollars(quote.amountCents)} por este mes de ${planLabel(quote.plan)}`;
  return { tone: 'due', status: 'Pago pendiente', detail, pay: true };
}
